/**
 * @file routes/_app/decisions/$id.tsx
 * @description 驗證決定詳情頁（M3）— 新審議流程。
 *
 * 業務流程：
 *  1. 安排審議（decision.arrange：admin/manager/auditor 主委, 狀態=待安排）：
 *     選 2 位委員 + 決議方式 → /arrange
 *  2. ZHSR07 表單填寫（審議成員於審議中；管理組另可於待安排/審議中預填，
 *     對應「初次申請證書資訊由管理組錄入」）→ /form 儲存 + /sign 簽署
 *  3. 簽署狀態展示（2 位委員是否已簽）
 *  4. 總經理核定（isManager, 狀態=待總經理核定）→ /gm-approve
 *
 * 視覺：PageShell + SectionCard + DataField
 *
 * 角色：
 *  - admin / manager：可安排、可填表（管理組錄入）、可核定
 *  - auditor（主委代表）：可安排
 *  - decider（且為本案委員）：可填表 + 簽署
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { ArrowLeft, CheckCircle, Gavel, Pen, UserCog, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import {
  PageShell,
  SectionCard,
  DataField,
  DataFieldGrid,
  StatusPill,
  PageMetaItem,
} from '@/components/shell';
import { color, space } from '@/styles/tokens';
import {
  DecisionFormEditor,
  type DecisionFormValue,
  type FollowUpActions,
} from '@/components/decision-form/DecisionFormEditor';
import {
  userCan,
  formatConclusionLabel,
  type DecisionScheme,
  type DossierSnapshot,
} from '@zhcb/shared';
import { DossierCard } from '@/components/decisions/DossierCard';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/decisions/$id')({ component: DecisionDetailPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

/** 已指派的委員會成員 */
interface CommitteeMember {
  id: number;
  memberId: number;
  memberName: string;
  assignedAt: string | null;
}

/** 委員簽署記錄 */
interface SignatureRecord {
  id: number;
  memberId: number;
  memberName: string;
  opinion: string | null;
  signedAt: string;
}

/** 驗證決定詳情（新形狀） */
interface DecisionDetail {
  id: number;
  code: string;
  caseId: number;
  /** 新狀態機：待安排 / 審議中 / 待總經理核定 / 已決_通過 / 已決_不通過 / 已決_延議 */
  status: string;
  scheme: DecisionScheme | null;
  reviewMethod: '書面' | '會議' | null;
  scheduledAt: string | null;
  arrangerId: number | null;
  /** 安排人姓名（需求：審議安排須留簽字＋日期） */
  arrangerName: string | null;
  arrangedAt: string | null;
  dueAt: string | null;
  deliberationNotes: string | null;
  /** JSONB：{ [sectionKey]: { [itemKey]: { result?, note? } }, certInfo: { [field]: string } } */
  formContent: Record<string, unknown> | null;
  conclusion: string | null;
  conclusionNote: string | null;
  riskLevel: '低' | '中' | '高' | null;
  /** §5.3 追蹤對策，後端以 jsonb 儲存 */
  followUpActions: FollowUpActions | null;
  gmDecision: '同意' | '其他' | null;
  gmNote: string | null;
  finalDecision: string | null;
  finalizedAt: string | null;
  /** 發證狀態（僅發證類結論才有值）：pending / succeeded / failed，失敗需人工重試 */
  certIssueStatus: 'pending' | 'succeeded' | 'failed' | null;
  /** 發證失敗原因（certIssueStatus='failed' 時有值） */
  certIssueError: string | null;
  /** 稽核文件包快照（報告核准移交時凍結；舊決定為 null） */
  dossierSnapshot: DossierSnapshot | null;
  /** 初審退回理由 / 操作者 / 時間（status='已退回' 時有值） */
  returnReason: string | null;
  returnedBy: number | null;
  returnedAt: string | null;
  /** 2 位審議委員 */
  members: CommitteeMember[];
  /** 簽署記錄 */
  signatures: SignatureRecord[];
  case: {
    caseCode: string;
    workflowState: string;
    clientId: number;
    verificationType: string;
    scheme: DecisionScheme | null;
    /** 序88：驗證決定基本資訊卡新增欄 */
    clientName: string | null;
    planCode: string | null;
    auditTeamNames: string | null;
    /** 現場稽核結論＝稽核報告【驗證建議】摘要（報告未定稿為 null） */
    reportRecommendation: string | null;
  } | null;
}

/** 委員候選人 */
interface CommitteeCandidate {
  id: number;
  name: string;
  email: string;
  /** 利益迴避：本案稽核人員不可被指派 */
  conflicted: boolean;
}

// ─── 狀態色調映射 ─────────────────────────────────────────────────────────────

const STATUS_TONE: Record<string, Parameters<typeof StatusPill>[0]['tone']> = {
  待安排: 'neutral',
  審議中: 'warning',
  待總經理核定: 'info',
  已決_通過: 'success',
  已決_不通過: 'danger',
  已決_延議: 'info',
  已退回: 'danger',
};

// ─── 工具函式 ────────────────────────────────────────────────────────────────

/**
 * 計算距離到期日剩餘天數。
 * @returns 負數表示已逾期，0 表示當天，正數表示剩餘天數
 */
function calcDaysUntilDue(dueAt: string | null): number | null {
  if (!dueAt) return null;
  return dayjs(dueAt).diff(dayjs().startOf('day'), 'day');
}

/**
 * 從 DecisionDetail 組合初始 DecisionFormValue。
 */
function buildInitialFormValue(decision: DecisionDetail): DecisionFormValue {
  const raw = decision.formContent;
  // certInfo 與各 section（process / group / result 等）並列存於 formContent 頂層，
  // 不存在 sections 包裹層——遍歷頂層 key 排除 certInfo 即可得到 sections map。
  const certInfo =
    raw && typeof raw === 'object' && 'certInfo' in raw
      ? (raw.certInfo as Record<string, string>)
      : {};
  const sections: DecisionFormValue['formContent'] = {};
  if (raw && typeof raw === 'object') {
    for (const [key, val] of Object.entries(raw)) {
      if (key !== 'certInfo') {
        sections[key] = val as Record<string, { result?: string; note?: string }>;
      }
    }
  }
  return {
    formContent: sections,
    deliberationNotes: decision.deliberationNotes ?? '',
    conclusion: decision.conclusion ?? undefined,
    conclusionNote: decision.conclusionNote ?? undefined,
    riskLevel: decision.riskLevel ?? undefined,
    certInfo,
    /** 還原追蹤對策；後端返回 null 時兜底空結構 */
    followUpActions: (decision.followUpActions as FollowUpActions) ?? { items: [] },
  };
}

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 驗證決定詳情頁主組件。
 */
function DecisionDetailPage() {
  const { id } = Route.useParams();
  const decisionId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user!);

  // 多角色：能力判斷用 roles
  const isManager = user.roles.includes('admin') || user.roles.includes('manager');
  const isDecider = user.roles.includes('decider');
  /** 主委職能：可安排審議（admin/manager/auditor，需求允許稽核員任主委） */
  const canArrangeCap = userCan(user, 'decision.arrange');

  /** 安排審議表單狀態 */
  const [arrangeMembers, setArrangeMembers] = useState<number[]>([]);
  const [arrangeMethod, setArrangeMethod] = useState<'書面' | '會議'>('書面');
  const [arrangeMeetingAt, setArrangeMeetingAt] = useState<dayjs.Dayjs | null>(null);

  /** ZHSR07 表單值（含 §5.3 追蹤對策） */
  const [formValue, setFormValue] = useState<DecisionFormValue>({
    formContent: {},
    deliberationNotes: '',
    certInfo: {},
    followUpActions: { items: [] },
  });

  /** 總經理核定表單 */
  const [gmForm] = Form.useForm<{ gmDecision: '同意' | '其他'; gmNote?: string }>();
  const gmDecisionWatch = Form.useWatch('gmDecision', gmForm);

  /** 手動終結後備 Modal */
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeForm] = Form.useForm<{ finalDecision: string; meetingMinutes?: string }>();

  // ─── 初審退回稽核報告 ───────────────────────────────────────────
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');

  // ─── 查詢決定詳情 ──────────────────────────────────────────────────
  const { data, isLoading } = useQuery<{ item: DecisionDetail }>({
    queryKey: ['decision', decisionId],
    queryFn: () => apiClient.get(`/api/decisions/${decisionId}`),
  });
  const decision = data?.item;

  // ─── 委員候選名單（具 decision.arrange 能力者才需要） ──────────────
  const { data: candidatesData } = useQuery<{ items: CommitteeCandidate[] }>({
    queryKey: ['committee-candidates', decisionId],
    queryFn: () => apiClient.get(`/api/decisions/committee-candidates?decisionId=${decisionId}`),
    enabled: canArrangeCap,
  });
  const candidates = candidatesData?.items ?? [];

  // 決定載入/變更後同步初始狀態
  useEffect(() => {
    if (!decision) return;
    // 同步安排委員初值
    setArrangeMembers(decision.members.map((m) => m.memberId));
    if (decision.reviewMethod) setArrangeMethod(decision.reviewMethod);
    if (decision.scheduledAt) setArrangeMeetingAt(dayjs(decision.scheduledAt));
    // 同步表單初值
    setFormValue(buildInitialFormValue(decision));
  }, [decision]);

  // ─── 安排審議 Mutation ─────────────────────────────────────────────
  const arrangeMutation = useMutation({
    mutationFn: (body: {
      memberIds: number[];
      reviewMethod: '書面' | '會議';
      scheduledAt?: string;
    }) => apiClient.post(`/api/decisions/${decisionId}/arrange`, body),
    onSuccess: () => {
      message.success('已安排審議');
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => message.error(e.message ?? '安排失敗'),
  });

  // ─── 儲存 ZHSR07 表單 Mutation ────────────────────────────────────
  const saveFormMutation = useMutation({
    mutationFn: (body: {
      deliberationNotes: string;
      formContent: Record<string, Record<string, { result?: string; note?: string }>>;
      conclusion?: string;
      conclusionNote?: string;
      riskLevel?: string;
      certInfo: Record<string, string>;
      /** §5.3 追蹤對策 checklist */
      followUpActions?: FollowUpActions;
    }) => apiClient.put(`/api/decisions/${decisionId}/form`, body),
    onSuccess: () => {
      message.success('表單已儲存');
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => message.error(e.message ?? '儲存失敗'),
  });

  // ─── 簽署 Mutation ────────────────────────────────────────────────
  const signMutation = useMutation({
    mutationFn: (body: { opinion?: string }) =>
      apiClient.post(`/api/decisions/${decisionId}/sign`, body),
    onSuccess: () => {
      message.success('已簽署');
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => message.error(e.message ?? '簽署失敗'),
  });

  // ─── 總經理核定 Mutation ──────────────────────────────────────────
  const gmMutation = useMutation({
    mutationFn: (body: { gmDecision: '同意' | '其他'; gmNote?: string }) =>
      apiClient.post(`/api/decisions/${decisionId}/gm-approve`, body),
    onSuccess: () => {
      message.success('已核定');
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => message.error(e.message ?? '核定失敗'),
  });

  // ─── 重試發證 Mutation（發證失敗補償） ────────────────────────────
  const retryIssueCertMutation = useMutation({
    mutationFn: () => apiClient.post(`/api/decisions/${decisionId}/retry-issue-certificate`, {}),
    onSuccess: () => {
      message.success('已重新觸發發證');
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => message.error(e.message ?? '重試發證失敗'),
  });

  // ─── 手動終結 Mutation（後備） ────────────────────────────────────
  const finalizeMutation = useMutation({
    mutationFn: (body: { finalDecision: string; meetingMinutes?: string }) =>
      apiClient.post(`/api/decisions/${decisionId}/finalize`, body),
    onSuccess: () => {
      message.success('決定已手動終結');
      setFinalizeOpen(false);
      finalizeForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => message.error(e.message ?? '終結失敗'),
  });

  const returnMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/decisions/${decisionId}/return-report`, {
        reason: returnReason.trim(),
      }),
    onSuccess: () => {
      void message.success('已退回稽核報告，主導稽核員將收到通知');
      setReturnOpen(false);
      setReturnReason('');
      void qc.invalidateQueries({ queryKey: ['decision', decisionId] });
    },
    onError: (e: Error) => void message.error(e.message || '退回失敗'),
  });

  // ─── 一鍵帶入證書資訊草稿 ─────────────────────────────────────────
  const loadCertInfoDraft = async () => {
    try {
      const res = await apiClient.get<{ item: Record<string, string> }>(
        `/api/decisions/${decisionId}/cert-info-draft`,
      );
      // 既有「非空」填寫值優先，draft 補其餘欄位（空字串不覆蓋草稿值，清空過的欄位也能帶回）
      setFormValue((v) => {
        const existing = Object.fromEntries(
          Object.entries(v.certInfo).filter(([, val]) => val != null && val !== ''),
        );
        return { ...v, certInfo: { ...res.item, ...existing } };
      });
      message.success('已帶入證書資訊草稿');
    } catch (e) {
      message.error((e as Error).message ?? '帶入失敗');
    }
  };

  /**
   * 發證前繳費確認（序104）：核定卡片顯示該案是否已有「已確認」繳費紀錄；
   * 未繳費時給出直達案件繳費頁的鏈接，供補錄入繳費資訊後再核定。
   * 僅在核定卡片可見（GM 視角）時拉取，避免無權限角色多打一次案件詳情。
   * ⚠️ Rules of Hooks：本 hook 必須位於下方 isLoading/!decision 早退**之前**——
   * 曾放在早退之後，decision 首次載入成功時 hooks 數量前後不一致，
   * React 報「change in the order of Hooks」。decision 未就緒時以 enabled=false 掛起。
   */
  const showGmCard = isManager && decision?.status === '待總經理核定';
  const casePaymentQuery = useQuery<{
    item: { payments?: { id: number; confirmedAt: string | null }[] } | null;
  }>({
    queryKey: ['decision-case-payments', decision?.caseId],
    queryFn: () => apiClient.get(`/api/cases/${decision?.caseId}`),
    enabled: !!decision && showGmCard,
  });

  // ─── Loading / 空狀態 ─────────────────────────────────────────────
  if (isLoading) return <div className="p-6">載入中...</div>;
  if (!decision) {
    return (
      <div className="p-6">
        <Alert type="error" message="決定記錄不存在" />
      </div>
    );
  }

  // ─── 衍生狀態 ─────────────────────────────────────────────────────
  const isFinalized = ['已決_通過', '已決_不通過', '已決_延議'].includes(decision.status);
  /** 當前登入用戶是否為本案委員 */
  const isCommitteeMember = decision.members.some((m) => m.memberId === user.id);
  /** 當前用戶是否已簽署 */
  const hasSigned = decision.signatures.some((s) => s.memberId === user.id);
  /** 日期距到期天數 */
  const daysUntilDue = calcDaysUntilDue(decision.dueAt);

  /** 決定的 scheme：優先用 decision.scheme，其次 case.scheme */
  const effectiveScheme: DecisionScheme = decision.scheme ?? decision.case?.scheme ?? 'TOP';
  /** 驗證類型 */
  const verificationType = decision.case?.verificationType ?? '';

  /** 表單是否可編輯：
   * - decider 且為本案委員且狀態=審議中；
   * - 或管理組（admin/manager）於待安排/審議中——對應需求
   *   「驗證證書資訊：初次申請由管理組錄入」，可在安排前先預填。 */
  const formEditable =
    (isDecider && isCommitteeMember && decision.status === '審議中') ||
    (isManager && ['待安排', '審議中'].includes(decision.status));

  /** 是否可安排：具 decision.arrange 且 status=待安排，或審議中且尚無任何簽署（允許重排）。
   * 一旦有委員已簽署即不可再重排，與後端守衛一致。 */
  const canArrange =
    canArrangeCap &&
    (decision.status === '待安排' ||
      (decision.status === '審議中' && decision.signatures.length === 0));

  /** 是否顯示安排卡片：可安排時才顯示 */
  const showArrangeCard = canArrange;

  // showGmCard（總經理核定卡片顯隱）與 casePaymentQuery 已提升至早退之前（Rules of Hooks）
  const confirmedPayments = (casePaymentQuery.data?.item?.payments ?? []).filter(
    (p) => p.confirmedAt,
  );

  /** 是否顯示手動終結按鈕（後備） */
  const canManualFinalize = isManager && !isFinalized;

  /** 初審退回：審議成員或主委，且決定仍在初審階段（待安排/審議中） */
  const canReturnReport =
    (isCommitteeMember || canArrangeCap) && ['待安排', '審議中'].includes(decision.status);

  return (
    <PageShell
      title={`驗證決定 — ${decision.code}`}
      meta={
        <>
          <StatusPill tone={STATUS_TONE[decision.status] ?? 'neutral'} size="sm">
            {decision.status}
          </StatusPill>
          {/* 到期提示 */}
          {daysUntilDue !== null && (
            <PageMetaItem
              label="到期"
              value={
                daysUntilDue < 0 ? (
                  <Tag color="red">已逾期 {Math.abs(daysUntilDue)} 天</Tag>
                ) : daysUntilDue === 0 ? (
                  <Tag color="orange">今日到期</Tag>
                ) : (
                  <Tag color="default">剩 {daysUntilDue} 天</Tag>
                )
              }
            />
          )}
        </>
      }
      actions={
        <Space>
          <Button
            icon={<ArrowLeft size={14} />}
            onClick={() => void navigate({ to: '/decisions' })}
          >
            返回列表
          </Button>
          {/* 手動終結後備按鈕 */}
          {canManualFinalize && (
            <Button danger icon={<Gavel size={14} />} onClick={() => setFinalizeOpen(true)}>
              手動終結
            </Button>
          )}
        </Space>
      }
    >
      {/* 已退回狀態提示 */}
      {decision.status === '已退回' && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="本決定已於初審退回稽核報告"
          description={
            <Space direction="vertical" size={4}>
              <span>退回理由：{decision.returnReason}</span>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {decision.returnedAt ? new Date(decision.returnedAt).toLocaleString() : ''}
                ・報告已回草稿，修改後重新定稿送審將開啟新一輪決定
              </Typography.Text>
            </Space>
          }
        />
      )}

      <Row gutter={[16, 16]}>
        {/* ── 左欄：基本資訊 + 安排卡片 + 關聯案件 + 最終結果 ── */}
        <Col xs={24} lg={10}>
          {/* 基本資訊 */}
          <div style={{ marginBottom: space[4] }}>
            {/* 序88：決議基本資訊 → 驗證決定基本資訊；排序 計劃編號/客戶名稱/驗證方案/
                現場稽核結論/稽核小組/審議狀態/審議安排/到期 */}
            <SectionCard icon={<Gavel size={14} />} title="驗證決定基本資訊">
              <DataFieldGrid columns={1}>
                <DataField label="計劃編號" value={decision.case?.planCode ?? decision.code} mono />
                <DataField
                  label="客戶名稱"
                  value={
                    decision.case?.clientName ?? <span style={{ color: color.text.muted }}>—</span>
                  }
                />
                <DataField label="決定編號" value={decision.code} mono />
                <DataField
                  label="現場稽核結論"
                  value={
                    decision.case?.reportRecommendation ? (
                      <Typography.Text style={{ fontSize: 13 }}>
                        {decision.case.reportRecommendation}
                      </Typography.Text>
                    ) : (
                      <span style={{ color: color.text.muted }}>（報告未定稿）</span>
                    )
                  }
                />
                <DataField
                  label="稽核小組"
                  value={
                    decision.case?.auditTeamNames ?? (
                      <span style={{ color: color.text.muted }}>—</span>
                    )
                  }
                />
                <DataField
                  label="審議狀態"
                  value={
                    <StatusPill tone={STATUS_TONE[decision.status] ?? 'neutral'} size="sm">
                      {decision.status}
                    </StatusPill>
                  }
                />
                <DataField
                  label="審議方式"
                  value={
                    decision.reviewMethod ?? <span style={{ color: color.text.muted }}>—</span>
                  }
                />
                <DataField
                  label="排程時間"
                  value={
                    decision.scheduledAt ? (
                      dayjs(decision.scheduledAt).format('YYYY-MM-DD HH:mm')
                    ) : (
                      <span style={{ color: color.text.muted }}>—</span>
                    )
                  }
                />
                {/* 審議安排簽記（需求：主委審議安排須留簽字＋日期） */}
                <DataField
                  label="審議安排"
                  value={
                    decision.arrangedAt ? (
                      <span>
                        {decision.arrangerName ?? '—'} ·{' '}
                        {dayjs(decision.arrangedAt).format('YYYY-MM-DD HH:mm')}
                      </span>
                    ) : (
                      <span style={{ color: color.text.muted }}>尚未安排</span>
                    )
                  }
                />
                {decision.dueAt && (
                  <DataField
                    label="到期日"
                    value={
                      <span
                        style={{
                          color: daysUntilDue !== null && daysUntilDue < 0 ? '#f5222d' : undefined,
                          fontWeight: daysUntilDue !== null && daysUntilDue < 0 ? 600 : undefined,
                        }}
                      >
                        {dayjs(decision.dueAt).format('YYYY-MM-DD')}
                        {daysUntilDue !== null &&
                          (daysUntilDue < 0
                            ? `（已逾期 ${Math.abs(daysUntilDue)} 天）`
                            : daysUntilDue === 0
                              ? '（今日到期）'
                              : `（剩 ${daysUntilDue} 天）`)}
                      </span>
                    }
                  />
                )}
                {decision.gmDecision && (
                  <DataField label="總經理決定" value={decision.gmDecision} />
                )}
                {decision.gmNote && <DataField label="總經理說明" value={decision.gmNote} />}
                {decision.finalDecision && (
                  <DataField
                    label="最終決定"
                    value={
                      <StatusPill
                        tone={
                          decision.finalDecision === '已決_通過' ||
                          decision.finalDecision === '通過'
                            ? 'success'
                            : decision.finalDecision === '已決_不通過' ||
                                decision.finalDecision === '不通過'
                              ? 'danger'
                              : 'info'
                        }
                        size="sm"
                      >
                        {decision.finalDecision}
                      </StatusPill>
                    }
                  />
                )}
                {decision.finalizedAt && (
                  <DataField
                    label="終結時間"
                    value={dayjs(decision.finalizedAt).format('YYYY-MM-DD HH:mm')}
                  />
                )}
              </DataFieldGrid>
            </SectionCard>
          </div>

          {/* 稽核文件包（移交快照）＋上報完整度（序85） */}
          <div style={{ marginBottom: space[4] }}>
            <DossierChecklistAlert caseId={decision.caseId} />
            <DossierCard snapshot={decision.dossierSnapshot} />
          </div>

          {/* 序：驗證決定管理移除【市售抽查統計】卡片（明細改於「風險評估>>市售抽樣查驗統計」查看） */}

          {/* 安排審議卡片（isManager, 待安排 或 審議中可重排） */}
          {showArrangeCard && (
            <div style={{ marginBottom: space[4] }}>
              <SectionCard icon={<Users size={14} />} title="安排審議">
                <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
                  {/* 委員多選：conflicted 置灰，強制恰好 2 位 */}
                  <Select<number[]>
                    mode="multiple"
                    placeholder="選擇審議委員（須恰好 2 位）"
                    value={arrangeMembers}
                    onChange={setArrangeMembers}
                    options={candidates.map((cand) => ({
                      label: cand.conflicted
                        ? `${cand.name}（${cand.email}）— 本案稽核，須迴避`
                        : `${cand.name}（${cand.email}）`,
                      value: cand.id,
                      disabled: cand.conflicted,
                    }))}
                    style={{ width: '100%' }}
                    optionFilterProp="label"
                    showSearch
                  />
                  {arrangeMembers.length !== 0 && arrangeMembers.length !== 2 && (
                    <Alert
                      type="warning"
                      message={`目前選了 ${arrangeMembers.length} 位，須恰好選 2 位委員`}
                      showIcon
                    />
                  )}

                  {/* 決議方式 */}
                  <div>
                    <div style={{ marginBottom: 8, color: color.text.secondary, fontSize: 13 }}>
                      決議方式
                    </div>
                    <Radio.Group
                      value={arrangeMethod}
                      onChange={(e) => setArrangeMethod(e.target.value as '書面' | '會議')}
                    >
                      <Radio value="書面">書面</Radio>
                      <Radio value="會議">會議</Radio>
                    </Radio.Group>
                  </div>

                  {/* 會議時間（僅決議方式=會議時顯示） */}
                  {arrangeMethod === '會議' && (
                    <DatePicker
                      showTime
                      value={arrangeMeetingAt}
                      onChange={setArrangeMeetingAt}
                      placeholder="選擇會議時間"
                      format="YYYY-MM-DD HH:mm"
                      style={{ width: '100%' }}
                    />
                  )}

                  <Button
                    type="primary"
                    icon={<Users size={14} />}
                    loading={arrangeMutation.isPending}
                    disabled={arrangeMembers.length !== 2}
                    onClick={() =>
                      arrangeMutation.mutate({
                        memberIds: arrangeMembers,
                        reviewMethod: arrangeMethod,
                        scheduledAt:
                          arrangeMethod === '會議' && arrangeMeetingAt
                            ? arrangeMeetingAt.toISOString()
                            : undefined,
                      })
                    }
                    block
                  >
                    {decision.status === '待安排' ? '確認安排' : '重新安排'}
                  </Button>
                </div>
              </SectionCard>
            </div>
          )}

          {/* 委員名單（唯讀展示） */}
          {!showArrangeCard && decision.members.length > 0 && (
            <div style={{ marginBottom: space[4] }}>
              <SectionCard icon={<Users size={14} />} title="審議委員名單">
                <Space size={[8, 8]} wrap>
                  {decision.members.map((m) => (
                    <Tag key={m.id} color="blue">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <UserCog size={12} />
                        {m.memberName}
                      </span>
                    </Tag>
                  ))}
                </Space>
              </SectionCard>
            </div>
          )}

          {/* 關聯案件 */}
          {decision.case && (
            <div style={{ marginBottom: space[4] }}>
              <SectionCard title="關聯案件">
                <DataFieldGrid columns={1}>
                  <DataField label="案件編號" value={decision.case.caseCode} mono />
                  <DataField label="案件狀態" value={<Tag>{decision.case.workflowState}</Tag>} />
                  <DataField label="驗證方案" value={effectiveScheme} />
                  <DataField
                    label="驗證類型"
                    value={verificationType || <span style={{ color: color.text.muted }}>—</span>}
                  />
                </DataFieldGrid>
              </SectionCard>
            </div>
          )}

          {/* 最終決定結果（已決） */}
          {isFinalized && (
            <Alert
              type={
                decision.status === '已決_通過'
                  ? 'success'
                  : decision.status === '已決_不通過'
                    ? 'error'
                    : 'warning'
              }
              icon={<CheckCircle size={16} />}
              showIcon
              message={`最終決定：${decision.finalDecision ?? decision.status}`}
            />
          )}

          {/* 發證失敗補償：總經理已核定通過但自動發證失敗，需人工重試 */}
          {decision.certIssueStatus === 'failed' && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: space[3] }}
              message="自動發證失敗"
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: space[2] }}>
                  <span>
                    總經理已核定通過，但系統自動發證失敗，案件尚未產生證書記錄：
                    {decision.certIssueError ?? '（未記錄錯誤原因）'}
                  </span>
                  {userCan(user, 'certificate.issue') && (
                    <Button
                      danger
                      size="small"
                      loading={retryIssueCertMutation.isPending}
                      onClick={() => retryIssueCertMutation.mutate()}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      重試發證
                    </Button>
                  )}
                </div>
              }
            />
          )}

          {/* 總經理核定卡片 */}
          {showGmCard && (
            <div style={{ marginTop: space[4] }}>
              <SectionCard icon={<Gavel size={14} />} title="總經理核定">
                {/* 審議結論摘要：GM「同意」= 核可下列結論，非固定「同意發放」——
                    結論可能是不同意發放/暫時停止/終止/撤銷等，須明示避免誤核 */}
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: space[3] }}
                  message={`審議結論：${decision.conclusion ? formatConclusionLabel(decision.conclusion) : '（未填）'}`}
                  description={decision.conclusionNote || undefined}
                />
                {/* 序104：發證前確認繳費——無已確認繳費紀錄時警示並提供錄入入口 */}
                {!casePaymentQuery.isLoading &&
                  (confirmedPayments.length > 0 ? (
                    <Alert
                      type="success"
                      showIcon
                      style={{ marginBottom: space[3] }}
                      message={`繳費確認：已有 ${confirmedPayments.length} 筆已確認繳費紀錄`}
                    />
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: space[3] }}
                      message="尚未查得已確認的繳費紀錄"
                      description={
                        <span>
                          發證前請確認客戶已完成繳費；若已收款，請先
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: '0 4px' }}
                            onClick={() =>
                              void navigate({
                                to: '/dispatch/$caseId',
                                params: { caseId: String(decision.caseId) },
                              })
                            }
                          >
                            前往案件錄入繳費資訊
                          </Button>
                          。
                        </span>
                      }
                    />
                  ))}
                <Form
                  form={gmForm}
                  layout="vertical"
                  onFinish={(values) => {
                    gmMutation.mutate(values);
                  }}
                >
                  <Form.Item
                    name="gmDecision"
                    label="核定結果"
                    rules={[{ required: true, message: '請選擇核定結果' }]}
                  >
                    <Radio.Group>
                      <Radio value="同意">同意（核可上述審議結論）</Radio>
                      <Radio value="其他">其他（附說明，退回審議）</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {gmDecisionWatch === '其他' && (
                    <Form.Item
                      name="gmNote"
                      label="說明"
                      rules={[{ required: true, message: '請輸入說明' }]}
                    >
                      <Input.TextArea rows={3} placeholder="請輸入說明..." maxLength={500} />
                    </Form.Item>
                  )}
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={gmMutation.isPending} block>
                      確認核定
                    </Button>
                  </Form.Item>
                </Form>
              </SectionCard>
            </div>
          )}
        </Col>

        {/* ── 右欄：ZHSR07 表單填寫器 + 簽署狀態 ── */}
        {/* 客戶 2026-07：簽署區移至「五、驗證決定結論」之後，以 CSS order 呈現（不動 DOM/邏輯） */}
        <Col xs={24} lg={14} style={{ display: 'flex', flexDirection: 'column' }}>
          {/* 簽署狀態區（order:2 → 顯示於表單之後） */}
          <div style={{ order: 2, marginTop: space[4] }}>
            <SectionCard icon={<Pen size={14} />} title="簽署狀態">
              <Space direction="vertical" size={space[3]} style={{ width: '100%' }}>
                {decision.members.length === 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="尚未安排審議委員"
                    description={
                      canArrangeCap
                        ? '請先於左側安排審議委員。'
                        : '請等待技術委員會主委（管理組／稽核員代表）安排審議委員。'
                    }
                  />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
                    {decision.members.map((member) => {
                      const sig = decision.signatures.find((s) => s.memberId === member.memberId);
                      return (
                        <div
                          key={member.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: space[3],
                            padding: `${space[3]}px`,
                            border: `1px solid ${color.border.subtle}`,
                            borderRadius: 8,
                          }}
                        >
                          <UserCog size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600 }}>{member.memberName}</div>
                            {sig ? (
                              <>
                                <Tag color="green" style={{ marginTop: 4 }}>
                                  <CheckCircle
                                    size={12}
                                    style={{ verticalAlign: 'middle', marginRight: 4 }}
                                  />
                                  已簽署
                                </Tag>
                                <div
                                  style={{
                                    color: color.text.tertiary,
                                    fontSize: 12,
                                    marginTop: 4,
                                  }}
                                >
                                  {dayjs(sig.signedAt).format('YYYY-MM-DD HH:mm')}
                                </div>
                                {sig.opinion && (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      color: color.text.secondary,
                                      fontSize: 13,
                                    }}
                                  >
                                    意見：{sig.opinion}
                                  </div>
                                )}
                              </>
                            ) : (
                              <Tag style={{ marginTop: 4 }}>未簽署</Tag>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {/* 本人委員簽署按鈕 */}
                    {isDecider &&
                      isCommitteeMember &&
                      !hasSigned &&
                      decision.status === '審議中' && (
                        <Button
                          type="primary"
                          icon={<Pen size={14} />}
                          loading={saveFormMutation.isPending || signMutation.isPending}
                          onClick={async () => {
                            // 先儲存表單（含追蹤對策）再簽署
                            await saveFormMutation.mutateAsync({
                              deliberationNotes: formValue.deliberationNotes,
                              formContent: formValue.formContent,
                              conclusion: formValue.conclusion,
                              conclusionNote: formValue.conclusionNote,
                              riskLevel: formValue.riskLevel,
                              certInfo: formValue.certInfo,
                              followUpActions: formValue.followUpActions,
                            });
                            signMutation.mutate({});
                          }}
                        >
                          儲存並簽署
                        </Button>
                      )}
                    {isDecider && hasSigned && (
                      <Alert type="success" showIcon message="您已完成簽署" />
                    )}
                    {isDecider && !isCommitteeMember && (
                      <Alert
                        type="info"
                        showIcon
                        message="您不在本案委員名單"
                        description="僅被指派的委員可對本決定填表與簽署。"
                      />
                    )}
                  </div>
                )}

                {/* 初審退回按鈕（審議成員或主委，且尚在初審階段） */}
                {canReturnReport && (
                  <Button danger onClick={() => setReturnOpen(true)} block>
                    初審不合格，退回報告
                  </Button>
                )}
              </Space>
            </SectionCard>
          </div>

          {/* ZHSR07 表單填寫器（客戶 2026-07 更名「驗證決定及批准意見」；order:1 置於簽署區之前） */}
          <div style={{ order: 1 }}>
            <SectionCard icon={<Pen size={14} />} title="驗證決定及批准意見">
              {/* 一鍵帶入證書資訊按鈕（表單可編輯者可見：管理組或本案審議成員） */}
              {formEditable && (
                <div style={{ marginBottom: space[3], textAlign: 'right' }}>
                  <Button size="small" onClick={() => void loadCertInfoDraft()}>
                    一鍵帶入證書資訊
                  </Button>
                </div>
              )}

              <DecisionFormEditor
                scheme={effectiveScheme}
                verificationType={verificationType}
                value={formValue}
                onChange={setFormValue}
                editable={formEditable}
              />

              {/* 儲存按鈕（僅可編輯時顯示） */}
              {formEditable && (
                <div style={{ marginTop: space[4] }}>
                  <Button
                    type="default"
                    loading={saveFormMutation.isPending}
                    onClick={() =>
                      saveFormMutation.mutate({
                        deliberationNotes: formValue.deliberationNotes,
                        formContent: formValue.formContent,
                        conclusion: formValue.conclusion,
                        conclusionNote: formValue.conclusionNote,
                        riskLevel: formValue.riskLevel,
                        certInfo: formValue.certInfo,
                        followUpActions: formValue.followUpActions,
                      })
                    }
                  >
                    儲存草稿
                  </Button>
                </div>
              )}
            </SectionCard>
          </div>
        </Col>
      </Row>

      {/* 手動終結 Modal（後備） */}
      <Modal
        title="手動終結決定"
        open={finalizeOpen}
        onCancel={() => setFinalizeOpen(false)}
        onOk={() => {
          void finalizeForm.validateFields().then((values) => {
            finalizeMutation.mutate(values);
          });
        }}
        confirmLoading={finalizeMutation.isPending}
        okText="確認終結"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <Alert
          type="warning"
          showIcon
          message="此操作將強制終結決定，請謹慎操作。"
          style={{ marginBottom: space[4] }}
        />
        <Form form={finalizeForm} layout="vertical">
          <Form.Item
            name="finalDecision"
            label="最終決定"
            rules={[{ required: true, message: '請選擇最終決定' }]}
          >
            <Select
              options={[
                { value: '通過', label: '通過' },
                { value: '不通過', label: '不通過' },
                { value: '延議', label: '延議' },
              ]}
              placeholder="選擇最終決定"
            />
          </Form.Item>
          <Form.Item name="meetingMinutes" label="會議記錄（選填）">
            <Input.TextArea rows={4} placeholder="請輸入會議紀要..." maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
      {/* 初審退回 Modal */}
      <Modal
        title="退回稽核報告"
        open={returnOpen}
        onCancel={() => setReturnOpen(false)}
        onOk={() => {
          if (!returnReason.trim()) {
            void message.warning('請填寫退回理由');
            return;
          }
          returnMutation.mutate();
        }}
        confirmLoading={returnMutation.isPending}
        okText="確認退回"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text>
            退回後：本決定終結為「已退回」，稽核報告回草稿由主導稽核員修改，
            修改後重新定稿送審將開啟新一輪決定。
          </Typography.Text>
          <Input.TextArea
            rows={4}
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            placeholder="例如：第三章證據不足，請補充現場照片與引用條款……"
            maxLength={2000}
            showCount
          />
        </Space>
      </Modal>
    </PageShell>
  );
}

// ─── 序85：上報文件完整度提示（決定詳情，管理組/審議成員可見） ──────────────────

/**
 * 稽核文件包上報完整度 Alert（序85）：
 * 表單完成數 / 應附文件分類覆蓋數；不完整時列出缺項提示驗證組補充。
 */
function DossierChecklistAlert({ caseId }: { caseId: number }) {
  const { data } = useQuery<{
    item: {
      forms: { templateCode: string | null; completedAt: string | null }[];
      categories: { key: string; uploaded: boolean }[];
      stats: {
        formsDone: number;
        formsTotal: number;
        docsDone: number;
        docsTotal: number;
        complete: boolean;
      };
    };
  }>({
    queryKey: ['dossier-checklist', caseId],
    queryFn: () => apiClient.get(`/api/cases/${caseId}/dossier-checklist`),
  });
  const item = data?.item;
  if (!item) return null;
  return (
    <Alert
      style={{ marginBottom: 12 }}
      type={item.stats.complete ? 'success' : 'warning'}
      showIcon
      message={`上報完整度：表單 ${item.stats.formsDone}/${item.stats.formsTotal}、應附文件 ${item.stats.docsDone}/${item.stats.docsTotal} 類`}
      description={
        !item.stats.complete && (
          <span style={{ fontSize: 12 }}>
            缺：
            {[
              ...item.forms.filter((f) => !f.completedAt).map((f) => f.templateCode ?? ''),
              ...item.categories.filter((c) => !c.uploaded).map((c) => c.key),
            ]
              .filter(Boolean)
              .join('、')}
            （不完整時請驗證組補充後再行審議）
          </span>
        )
      }
    />
  );
}
